import { getIDFromUrl } from '@rossum/api-client';
import { Annotation } from '@rossum/api-client/annotations';
import { DocumentRelation } from '@rossum/api-client/documentRelations';
import { DetailDrawer } from '@rossum/ui';
import { IconCloudDownload } from '@rossum/ui/icons/tabler';
import {
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
} from '@rossum/ui/material';
import { Fragment } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import * as R from 'remeda';
import { annotationSideloadsSelector } from '../../../redux/modules/annotation/selectors';
import { useDownloadOriginalDocuments } from '../../tasks/hooks/useDownloadOriginalDocuments';
import { useFetchDocuments } from './hooks/useFetchDocuments';

type FilesDrawerProps = {
  annotation: Annotation;
  documentRelations: DocumentRelation[];
  onClose: () => void;
};

export const FilesDrawer = ({
  annotation,
  documentRelations,
  onClose,
}: FilesDrawerProps) => {
  const intl = useIntl();
  const { mutate: downloadOriginalDocuments } = useDownloadOriginalDocuments();
  const annotationSideloads = useSelector(annotationSideloadsSelector);
  const originalDocument = annotationSideloads.document;

  // TODO: ask BE for documentRelations?sidelaod=documents
  const documentsIds = documentRelations.flatMap(({ documents }) =>
    documents.map(d => getIDFromUrl(d))
  );

  const { data } = useFetchDocuments({ documentIds: documentsIds });

  const documents = [
    data?.results ? data.results : [],
    originalDocument ?? [],
  ].flat();

  const releationsDocuments = R.indexBy(documents ?? [], item => item.url);

  const originalDocumentRelation = originalDocument
    ? {
        type: 'orginal',
        key: 'original_key',
        id: 'original_id',
        documents: [originalDocument.url],
        annotation: annotation.url,
      }
    : undefined;

  const relations = [
    originalDocumentRelation ?? [],
    ...documentRelations,
  ].flat();

  return (
    <DetailDrawer
      PaperProps={{ elevation: 2 }}
      open
      onClose={onClose}
      title={
        <Stack component="span" spacing={2} direction="row">
          {intl.formatMessage({
            id: 'components.fileDrawer.title',
          })}
        </Stack>
      }
    >
      <List sx={{ padding: 2 }}>
        {relations.map(relation => (
          <Fragment key={relation.id}>
            {relation.documents.map(documentUrl => {
              const document = releationsDocuments[documentUrl];
              if (!document) return null;

              return (
                <ListItem
                  key={document.id}
                  secondaryAction={
                    <IconButton
                      className="hover-button"
                      edge="end"
                      onClick={() =>
                        downloadOriginalDocuments({
                          documents: [document.url],
                          type: 'document',
                        })
                      }
                    >
                      <IconCloudDownload />
                    </IconButton>
                  }
                  divider
                  sx={{
                    '& .hover-button': {
                      opacity: 0,
                      transition: 'opacity 0.4s',
                    },
                    '&:hover .hover-button': {
                      opacity: 1,
                    },
                    bgcolor: 'action.hover',
                    mb: 1,
                    '&:hover': { bgcolor: 'action.hover' },
                    borderRadius: 1,
                  }}
                >
                  <ListItemText primary={document.originalFileName} />
                </ListItem>
              );
            })}
          </Fragment>
        ))}
      </List>
    </DetailDrawer>
  );
};
